export const JUSTINIANO_LAYOUT_STORAGE_KEY = "tribunalea:justiniano-layout";
export const JUSTINIANO_LAYOUT_EVENT = "justiniano:layout-change";

const JUSTINIANO_LAYOUT_COOKIE_KEY = "tribunalea_justiniano_layout";
const JUSTINIANO_LAYOUT_COOKIE_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
const MIN_STORED_WIDTH = 280;
const MAX_STORED_WIDTH = 2_000;
const MIN_STORED_HEIGHT = 360;
const MAX_STORED_HEIGHT = 2_000;

export const DEFAULT_JUSTINIANO_LAYOUT = Object.freeze({
  docked: false,
  width: 408,
  floatingHeight: 680,
});

function finiteNumber(value, fallback, minimum, maximum) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.round(Math.min(maximum, Math.max(minimum, number)));
}

export function normalizeJustinianoLayout(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return {
    docked: value.docked === true,
    width: finiteNumber(
      value.width,
      DEFAULT_JUSTINIANO_LAYOUT.width,
      MIN_STORED_WIDTH,
      MAX_STORED_WIDTH,
    ),
    floatingHeight: finiteNumber(
      value.floatingHeight,
      DEFAULT_JUSTINIANO_LAYOUT.floatingHeight,
      MIN_STORED_HEIGHT,
      MAX_STORED_HEIGHT,
    ),
  };
}

function parseLayout(value) {
  if (!value) return null;
  try {
    return normalizeJustinianoLayout(JSON.parse(value));
  } catch {
    return null;
  }
}

function cookieLayout(cookie = "") {
  const entry = String(cookie)
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${JUSTINIANO_LAYOUT_COOKIE_KEY}=`));
  if (!entry) return null;
  try {
    return parseLayout(decodeURIComponent(entry.split("=").slice(1).join("=")));
  } catch {
    return null;
  }
}

export function readJustinianoLayoutPreference({
  cookie = typeof document !== "undefined" ? document.cookie : "",
  storage = typeof window !== "undefined" ? window.localStorage : null,
} = {}) {
  const shared = cookieLayout(cookie);
  if (shared) return shared;
  try {
    return parseLayout(storage?.getItem(JUSTINIANO_LAYOUT_STORAGE_KEY))
      || { ...DEFAULT_JUSTINIANO_LAYOUT };
  } catch {
    return { ...DEFAULT_JUSTINIANO_LAYOUT };
  }
}

export function persistJustinianoLayoutPreference(layout, {
  documentObject = typeof document !== "undefined" ? document : null,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  protocol = typeof window !== "undefined" ? window.location.protocol : "",
  storage = typeof window !== "undefined" ? window.localStorage : null,
} = {}) {
  const next = normalizeJustinianoLayout(layout)
    || { ...DEFAULT_JUSTINIANO_LAYOUT };
  const serialized = JSON.stringify(next);
  try {
    storage?.setItem(JUSTINIANO_LAYOUT_STORAGE_KEY, serialized);
  } catch {
    // Cookies still keep the preference shared when local storage is blocked.
  }
  if (documentObject) {
    const sharedDomain = hostname === "tribunalea.com"
      || hostname.endsWith(".tribunalea.com")
      ? "; Domain=.tribunalea.com"
      : "";
    const secure = protocol === "https:" ? "; Secure" : "";
    documentObject.cookie = `${JUSTINIANO_LAYOUT_COOKIE_KEY}=${encodeURIComponent(serialized)}`
      + `; Path=/; Max-Age=${JUSTINIANO_LAYOUT_COOKIE_MAX_AGE_SECONDS}`
      + `; SameSite=Lax${secure}${sharedDomain}`;
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(JUSTINIANO_LAYOUT_EVENT, {
      detail: next,
    }));
  }
  return next;
}
